import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { User } from '@org/interfaces';
import { SysNotifyService, UserService, GmAssignmentService, AuthService } from '@org/shared-services';
import { FormsModule } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'app-team',
    imports: [CommonModule, FormsModule],
    templateUrl: './team.component.html',
    styleUrl: './team.component.css',
    animations: [
        trigger('slideInOut', [
            state('in', style({
                transform: 'translateX(0)'
            })),
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('200ms ease-in')
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
            ])
        ])
    ]
})
export class TeamComponent implements OnInit {
  users: User[] = [];
  user!: User;
  selectedUser: User | null = null;
  roles: string[] = ['admin', 'gm', 'developer', 'tech', 'estimator', 'parts coordinator'];
  tenantId!: string;
  filteredUsers: User[] = [];
  searchQuery = '';
  editUserVisible = false;
  addUserVisible = false;
  allRoles: any[] = [];
  currentTenant: any = null;
  selectedRoleId = '';
  newRoleId = '';
  showNewPassword = false;
  addUserError = '';
  addConflict: { code: 'removed' | 'other-shop'; name: string; shopName?: string } | null = null;
  createdPassword: string | null = null;
  createdUserName = '';
  createdTitle = 'User created';
  createdNotice = '';
  passwordCopied = false;

  newUser: any = {
    name: '',
    email: '',
    password: '',
    tenantId: ''
  };

 
  constructor(
    private userService: UserService, 
    private sysNotify: SysNotifyService,
    private gmAssignmentService: GmAssignmentService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.user = this.authService.currentUserValue;
      if (this.user.roles && this.user.roles.includes('gm')) {
        this.gmAssignmentService.getCurrentTenant().subscribe((res: any) => {
          this.currentTenant = res.data.tenant;
          this.loadUsers(this.currentTenant);
        });
      } else {
        this.currentTenant = this.user?.tenant;
        this.loadUsers(this.currentTenant);
      }
    
  }

  loadUsers(tenantId: string): void {
    this.userService.getUsersByTenant(tenantId).subscribe(users => {
      this.users = users.filter((user: { roles: any[]; }) => !user.roles.some(role => role.name === 'developer'));
      this.filteredUsers = this.users;
    });
  }

  filterUsers(): void {
    if (this.searchQuery.trim() === '') {
      this.filteredUsers = this.users;
    } else {
      this.filteredUsers = this.users.filter(user => {
        return user.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
               user.email.toLowerCase().includes(this.searchQuery.toLowerCase()) &&
               !user.roles.some(role => role.name === 'developer')
      });
    }
  }

  editUser(user: User): void {
    this.editUserVisible = true;
    this.addUserVisible = false;
    this.selectedUser = { ...user, roles: [...(user.roles || [])] };
    this.selectedRoleId = this.roleIdFor(user);
    this.loadRoles();
  }

  saveUser(): void {
    if (!this.selectedUser || !this.selectedRoleId) {
      return;
    }
    const payload = { ...this.selectedUser, roles: [this.selectedRoleId] };
    this.userService.updateUser(payload).subscribe({
      next: () => {
        this.loadUsers(this.getTenantContext());
        this.selectedUser = null;
        this.selectedRoleId = '';
        this.editUserVisible = false;
      },
      error: () => {
        alert('Failed to save user');
      }
    });
  }

  get passwordPolicy() {
    const value = this.newUser?.password || '';
    const length = value.length >= 8;
    const letter = /[A-Za-z]/.test(value);
    const number = /[0-9]/.test(value);
    return { length, letter, number, valid: length && letter && number };
  }

  addUser(confirm?: 'restore' | 'transfer'): void {
    if (!this.newUser || !this.newRoleId || !this.passwordPolicy.valid) {
      return;
    }
    this.addUserError = '';
    const sharedPassword = this.newUser.password;
    const sharedName = this.newUser.name;
    const conflict = this.addConflict;
    const payload = {
      ...this.newUser,
      roles: [this.newRoleId],
      tenantId: this.getTenantContext(),
      ...(confirm ? { confirm } : {})
    };
    this.userService.register(payload).subscribe({
      next: () => {
        this.loadUsers(this.getTenantContext());
        this.resetUser();
        this.addUserVisible = false;
        this.addConflict = null;
        this.createdUserName = sharedName;
        this.createdPassword = sharedPassword;
        this.passwordCopied = false;
        this.createdTitle = 'User created';
        this.createdNotice = this.createdNoticeFor(confirm, sharedName, conflict?.shopName);
      },
      error: (error) => {
        const body = error?.error || {};
        if (body.code === 'same-shop') {
          this.addUserError = 'This person is already on your team.';
          return;
        }
        if (body.code === 'removed' || body.code === 'other-shop') {
          this.addConflict = {
            code: body.code,
            name: body.name || sharedName,
            shopName: body.shopName
          };
          return;
        }
        const message = body.message || 'Failed to add user';
        this.addUserError = message;
        this.sysNotify.showError(message);
      }
    });
  }

  confirmExistingUser(): void {
    if (!this.addConflict) {
      return;
    }
    const confirm = this.addConflict.code === 'removed' ? 'restore' : 'transfer';
    this.addUser(confirm);
  }

  cancelExistingUser(): void {
    this.addConflict = null;
  }

  private createdNoticeFor(confirm: 'restore' | 'transfer' | undefined, name: string, shopName?: string): string {
    if (confirm === 'restore') {
      return `${name} has been added back to this shop. Share this password with them. It will not be shown again.`;
    }
    if (confirm === 'transfer') {
      return `${name} has been moved from ${shopName || 'their previous shop'}. Share this password with them. They will no longer appear on that shop's team.`;
    }
    return `Share this password with ${name}. It is not emailed and will not be shown again.`;
  }

  resetUser(): void {
    this.newRoleId = '';
    this.showNewPassword = false;
    this.addUserError = '';
    this.addConflict = null;
    this.newUser = {
      name: '',
      email: '',
      password: '',
      tenantId: this.getTenantContext()
    };
  }

  async copyCreatedPassword(): Promise<void> {
    if (!this.createdPassword) {
      return;
    }
    try {
      await navigator.clipboard.writeText(this.createdPassword);
    } catch {
      const field = document.createElement('textarea');
      field.value = this.createdPassword;
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      document.body.removeChild(field);
    }
    this.passwordCopied = true;
  }

  dismissCreatedPassword(): void {
    this.createdPassword = null;
    this.createdUserName = '';
    this.createdNotice = '';
    this.createdTitle = 'User created';
    this.passwordCopied = false;
  }

  resetPassword(person: User | null | undefined): void {
    const userId = person?._id;
    if (!userId) {
      this.sysNotify.showError('Failed to reset password');
      return;
    }
    this.userService.resetPassword(userId).subscribe({
      next: (res: { message?: string; temporaryPassword?: string }) => {
        const temp = res?.temporaryPassword;
        if (!temp) {
          this.sysNotify.showSuccess('Password reset successfully');
          return;
        }
        this.createdUserName = person?.name || 'this user';
        this.createdPassword = temp;
        this.passwordCopied = false;
        this.createdTitle = 'Password reset';
        this.createdNotice = `Share this password with ${this.createdUserName}. Ask them to change it after login. It will not be shown again.`;
      },
      error: () => {
        this.sysNotify.showError('Failed to reset password');
      }
    });
  }

  closeModal(): void {
    this.selectedUser = null;
  }

  loadRoles() {
    this.userService.getAssignableRoles().subscribe((roles: any[]) => {
      this.allRoles = roles;
      if (this.selectedUser && !this.selectedRoleId) {
        this.selectedRoleId = this.roleIdFor(this.selectedUser);
      }
    });
  }

  private roleIdFor(user: User): string {
    const role = user?.roles?.[0];
    if (!role) {
      return '';
    }
    if (typeof role === 'string') {
      return this.allRoles.find((item) => item.name === role)?._id || '';
    }
    if (role._id) {
      return role._id;
    }
    return this.allRoles.find((item) => item.name === role.name)?._id || '';
  }

  namebkC(user: any): string {
    return this.userService.getBackgroundColor(user?.name);
  }
    
  nameinitial(user: any): string {
    return this.userService.getInitials(user?.name);
  } 

  toggleEditUser(): void {
    this.editUserVisible = !this.editUserVisible;
  }

  toggleAddUser(): void {
    this.addUserVisible = !this.addUserVisible;
    if (this.addUserVisible) {
      this.loadRoles();
    }
  }

  deleteUser(user: any) {
    if (confirm('Are you sure you want to delete this user?')) {
      user.isActive = false;
      this.userService.updateUser(user).subscribe({
        next: (response) => {
          alert('User deleted successfully.');
          // Refresh the user list after deletion
          this.editUserVisible = false;
          this.loadUsers(this.getTenantContext());
        },
        error: (error) => {
          console.error('Error deleting user:', error);
          alert('Failed to delete user.');
        }
    });
    }
  }

  private getTenantContext(): string {
    if (this.user && this.user.roles && this.user.roles.includes('gm')) {
      return this.currentTenant!;
    }
    return this.user?.tenant;
  }
}
